import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { FormBuilderComponent } from '../../../components/form-builder/form-builder.component';
import { DynamicForm, FileRepresentation, SelectOption } from '../../../components/form-builder/form-builder.types';
import { ButtonModule } from 'primeng/button';
import { PageHeaderComponent } from '../../../components/shared/page-header/page-header.component';
import { AgpmRepository } from '../../../store/agpm-state/agpm-state.repository';
import { AgpmService } from '../agpm.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { getCreateAgpmFormObject } from './utils/create-agpm-form-object';
import { MessageService } from 'primeng/api';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FileService, NAMESPACE_ENUM } from '@/components/shared/file-list/file-list.service';
import { ContractingAuthorityService } from '@/pages/settings/contracting-authority/contracting-authority.service';
import { AgpmWorkflowBlocksService } from '../agpm-workflow-blocks.service';
import { DynamicBlocks } from '@/components/block-builder/block-builder.types';
import { BlockBuilderComponent } from '../../../components/block-builder/block-builder.component';
import { StatusComponent } from '../../../components/shared/status/status.component';
import { agpmCreateFormSchema } from '@/validations/agpm/create-form.validation';

@Component({
  selector: 'sm-agpm-new-notice',
  imports: [
    FormBuilderComponent,
    ButtonModule,
    PageHeaderComponent,
    CommonModule,
    FormsModule,
    InputTextModule,
    TranslateModule,
    BlockBuilderComponent,
    StatusComponent,
  ],
  templateUrl: './new-notice.component.html',
  styleUrl: './new-notice.component.scss',
})
export class AgpmNewNoticeComponent implements OnInit, OnDestroy {
  agpmRepository = inject(AgpmRepository);
  agpmService = inject(AgpmService);
  agpmWorkflowBlocksService = inject(AgpmWorkflowBlocksService);
  contractingAuthorityService = inject(ContractingAuthorityService);
  messageService = inject(MessageService);
  router = inject(Router);
  translate = inject(TranslateService);
  fileService = inject(FileService);

  breadcrumb = [
    {
      label: 'agpm.plural',
      routerLink: ['/agpm-current-notice'],
    },
    {
      label: 'agpm.form.addTitle',
      routerLink: ['agpm-new-notice'],
    },
  ];
  loading = false;

  formObject: DynamicForm = getCreateAgpmFormObject({
    store: this.agpmRepository,
    download: (file: FileRepresentation) => this.fileService.download(file, NAMESPACE_ENUM.AGPM),
    acOptions: [],
    resetTmpFiles: resetFn => {
      this.uploaderResetFn = resetFn;
    },
  });
  controlBlocks: DynamicBlocks = this.agpmWorkflowBlocksService.getPreDraftAgpmBlocks({
    save: () => this.save(),
    reset: () => this.reset(),
  });
  acOptions: SelectOption[] = [];

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  uploaderResetFn: () => void = () => {};

  ngOnInit() {
    this.agpmRepository.reset();

    this.contractingAuthorityService.getCurrentUserContractingAuthority().subscribe(data => {
      this.acOptions = data.map(ac => {
        return { name: ac.nom, code: ac.id };
      });
      this.formObject = getCreateAgpmFormObject({
        store: this.agpmRepository,
        download: (file: FileRepresentation) => this.fileService.download(file, NAMESPACE_ENUM.AGPM),
        acOptions: this.acOptions,
        resetTmpFiles: resetFn => {
          this.uploaderResetFn = resetFn;
        },
      });
    });
  }

  ngOnDestroy(): void {
    this.agpmRepository.reset();
  }

  save() {
    const result = agpmCreateFormSchema.safeParse(this.agpmRepository.getNested('dto'));
    if (result.success) {
      const agpmFormData = this.agpmRepository.getDtoFormData(false);
      this.loading = true;
      agpmCreateFormSchema.safeParse(agpmFormData);
      this.agpmService.addAgpm(agpmFormData).subscribe({
        next: data => {
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: this.translate.instant('agpm.message.success.added'),
          });
          this.router.navigate(['/agpm-edit-notice', data.id]);
          this.loading = false;
        },
        error: err => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: err.error.message || this.translate.instant('agpm.message.error.added'),
          });
          this.loading = false;
        },
      });
    } else {
      this.agpmRepository.setErrors(result.error.flatten().fieldErrors);
    }
  }

  reset() {
    this.agpmRepository.reset();
    this.uploaderResetFn?.();
  }
}
